import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Typography,
  useTheme,
  Grow,
  Slide,
} from '@mui/material'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useEffect, useMemo } from 'react'
import * as React from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material'
import {
  CertificateIcon,
  DownloadIcon,
  SearchIcon,
  UserIcon,
} from 'src/v3/core/icons'
import { downloadFile } from '../../utilities/filesystem'
import {
  fetchLinkedServices,
  fetchUsers,
  fetchUserHours,
  fetchCertificates,
  fetchLiveCertificates,
  fetchExternalCertificates,
  fetchCredentials,
  fetchCompletedCourses,
  fetchSections,
  fetchCourses,
  fetchGroups,
  fetchLicenses,
  fetchAssignments,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectUsersStatus,
  selectUserHoursStatus,
  selectCertificatesStatus,
  selectLiveCertificatesStatus,
  selectExternalCertificatesStatus,
  selectCredentialsStatus,
  selectCompletedCoursesStatus,
  selectSectionsStatus,
  selectGroupsStatus,
  selectLicenses,
  selectAssignmentsStatus,
} from '../manage.slice'

// Import Redux analytics infrastructure
import {
  // Actions
  setSelectedLinkedServiceID,
  setErrorMessage,
  clearMessages,
  setSelectedUserForDetails,
  setUserDetailsOpen,
  setSelectedDayData,
  setDayActivitiesOpen,
  setSelectedAssignmentEvent,
  setAssignmentEventOpen,
  setDownloading,
  setExportingCsv,
  processAnalyticsCEData,
  // Selectors
  selectSelectedLinkedServiceID,
  selectViewMode,
  selectTimeRange,
  selectCustomDateRange,
  selectSelectedUsers,
  selectSelectedCategories,
  selectSelectedLicenseTypes,
  selectSelectedGroups,
  selectCeActivities,
  selectTimelineData,
  selectKpiData,
  selectIsProcessingData,
  selectErrorMessage,
  selectAlertMessage,
  selectDownloadState,
  selectIsLoadingData,
} from './advanced-activity-dashboard/index'

// Import components
import FilterPanel from './advanced-activity-dashboard/components/filters/FilterPanel'
import UserDetailsModal from './advanced-activity-dashboard/components/modals/UserDetailsModal'
import DayActivitiesModal from './advanced-activity-dashboard/components/modals/DayActivitiesModal'
import AssignmentEventModal from './advanced-activity-dashboard/components/modals/AssignmentEventModal'
import AnalyticsView from './advanced-activity-dashboard/components/views/AnalyticsView'
import SummaryView from './advanced-activity-dashboard/components/views/SummaryView'
import ExportView from './advanced-activity-dashboard/components/views/ExportView'

dayjs.extend(relativeTime)

export default () => {
  let theme = useTheme()
  let dispatch = useAppDispatch()

  // Redux state selectors - replacing all useState hooks
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let selectedLinkedServiceID = useAppSelector(selectSelectedLinkedServiceID)

  let usersStatus = useAppSelector(selectUsersStatus)

  // CE Data from Redux
  let userHoursStatus = useAppSelector(selectUserHoursStatus)
  let recordedCertificatesStatus = useAppSelector(selectCertificatesStatus)
  let liveCertificatesStatus = useAppSelector(selectLiveCertificatesStatus)
  let externalCertificatesStatus = useAppSelector(
    selectExternalCertificatesStatus,
  )
  let credentialsStatus = useAppSelector(selectCredentialsStatus)
  let completedCoursesStatus = useAppSelector(selectCompletedCoursesStatus)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let coursesStatus = useAppSelector((state) => state.manage.coursesStatus)

  // Groups data from Redux
  let groupsStatus = useAppSelector(selectGroupsStatus)

  // Licenses data from Redux
  let licenses = useAppSelector(selectLicenses) as Record<string, any[]>
  let licensesByService = licenses[selectedLinkedServiceID] || []

  // Assignments data from Redux
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)

  // Analytics Redux state - only what's needed for main component
  let viewMode = useAppSelector(selectViewMode)
  let errorMessage = useAppSelector(selectErrorMessage)
  let alertMessage = useAppSelector(selectAlertMessage)

  // Data state from Redux
  let timelineData = useAppSelector(selectTimelineData)
  let ceActivities = useAppSelector(selectCeActivities)
  let kpiData = useAppSelector(selectKpiData)

  // Download state from Redux
  let downloadState = useAppSelector(selectDownloadState)
  let { exportingCsv } = downloadState

  // Computed state from Redux selectors
  let isProcessingData = useAppSelector(selectIsProcessingData)
  let isLoadingData = useAppSelector(selectIsLoadingData)

  // Initialize data - same as original
  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus, dispatch])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus, dispatch])

  useEffect(() => {
    if (groupsStatus === 'uninitialized') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus, dispatch])

  useEffect(() => {
    if (userHoursStatus === 'uninitialized') {
      dispatch(fetchUserHours())
    }
  }, [userHoursStatus, dispatch])

  useEffect(() => {
    if (recordedCertificatesStatus === 'uninitialized') {
      dispatch(fetchCertificates())
    }
  }, [recordedCertificatesStatus, dispatch])

  useEffect(() => {
    if (liveCertificatesStatus === 'uninitialized') {
      dispatch(fetchLiveCertificates())
    }
  }, [liveCertificatesStatus, dispatch])

  useEffect(() => {
    if (externalCertificatesStatus === 'uninitialized') {
      dispatch(fetchExternalCertificates())
    }
  }, [externalCertificatesStatus, dispatch])

  useEffect(() => {
    if (credentialsStatus === 'uninitialized') {
      dispatch(fetchCredentials())
    }
  }, [credentialsStatus, dispatch])

  useEffect(() => {
    if (completedCoursesStatus === 'uninitialized') {
      dispatch(fetchCompletedCourses())
    }
  }, [completedCoursesStatus, dispatch])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus, dispatch])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus, dispatch])

  useEffect(() => {
    if (selectedLinkedServiceID && !licensesByService.length) {
      dispatch(fetchLicenses(selectedLinkedServiceID))
    }
  }, [selectedLinkedServiceID, licensesByService.length, dispatch])

  useEffect(() => {
    if (assignmentsStatus === 'uninitialized') {
      dispatch(fetchAssignments())
    }
  }, [assignmentsStatus, dispatch])

  useEffect(() => {
    if (!selectedLinkedServiceID && (linkedServices as any[]).length > 0) {
      dispatch(setSelectedLinkedServiceID((linkedServices as any[])[0]?.id))
    }
  }, [linkedServices, selectedLinkedServiceID, dispatch])

  // Check if essential data is loaded (allow some failures)
  let essentialDataLoaded = useMemo(() => {
    let coreDataLoaded =
      recordedCertificatesStatus !== 'uninitialized' &&
      recordedCertificatesStatus !== 'pending' &&
      externalCertificatesStatus !== 'uninitialized' &&
      externalCertificatesStatus !== 'pending' &&
      completedCoursesStatus !== 'uninitialized' &&
      completedCoursesStatus !== 'pending' &&
      assignmentsStatus !== 'uninitialized' &&
      assignmentsStatus !== 'pending' &&
      coursesStatus !== 'uninitialized' &&
      coursesStatus !== 'pending'

    return coreDataLoaded
  }, [
    recordedCertificatesStatus,
    externalCertificatesStatus,
    completedCoursesStatus,
    assignmentsStatus,
    coursesStatus,
  ])

  // Track the last time we processed data to prevent infinite loops
  let lastProcessedRef = React.useRef<string>('')

  // Get filter selectors for useEffect dependencies
  let selectedUsers = useAppSelector(selectSelectedUsers)
  let selectedCategories = useAppSelector(selectSelectedCategories)
  let selectedLicenseTypes = useAppSelector(selectSelectedLicenseTypes)
  let selectedGroups = useAppSelector(selectSelectedGroups)
  let timeRange = useAppSelector(selectTimeRange)
  let customDateRange = useAppSelector(selectCustomDateRange)

  // Single useEffect to process CE data when conditions are met
  useEffect(() => {
    if (selectedLinkedServiceID && essentialDataLoaded && !isProcessingData) {
      let currentStateHash = [
        selectedLinkedServiceID,
        selectedUsers.join(','),
        selectedCategories.join(','),
        selectedLicenseTypes.join(','),
        selectedGroups.join(','),
        timeRange,
        customDateRange.startDate,
        customDateRange.endDate,
      ].join('|')

      if (currentStateHash !== lastProcessedRef.current) {
        // eslint-disable-next-line fp/no-mutation
        lastProcessedRef.current = currentStateHash
        dispatch(processAnalyticsCEData())
      }
    }
  }, [
    selectedLinkedServiceID,
    essentialDataLoaded,
    selectedUsers,
    selectedCategories,
    selectedLicenseTypes,
    selectedGroups,
    timeRange,
    customDateRange.startDate,
    customDateRange.endDate,
    isProcessingData,
    dispatch,
  ])

  // Grid columns for export view

  // Certificate download function
  let getCertificate = (id: string, type: string) =>
    axios
      .get(
        `/api/v2/manage/certificate/export?certificateID=${id}&certificateType=${type}`,
      )
      .then(pluckData)

  let downloadCertificate = (
    certificateId: string,
    activityType: string,
    fileName: string,
  ) => {
    dispatch(setDownloading(certificateId))
    getCertificate(certificateId, activityType.toLowerCase())
      .then((response) =>
        axios.get(response, { responseType: 'blob' }).catch(() => {
          // eslint-disable-next-line fp/no-throw, no-throw-literal
          throw 'File Not Found'
        }),
      )
      .then(pluckData)
      .then((response) =>
        downloadFile(response, `${fileName || certificateId}.pdf`),
      )
      .then(() => dispatch(setDownloading('')))
      .catch((err) => {
        dispatch(setDownloading(''))
        dispatch(
          setErrorMessage(`PDF Download Failed: ${err.response?.data || err}`),
        )
      })
  }

  // Helper function to describe active filters - using Redux selector

  // CSV export function for filtered CE activities
  let exportFilteredActivitiesCSV = () => {
    if (ceActivities.length === 0) {
      dispatch(setErrorMessage('No activities to export'))
      return
    }

    dispatch(setExportingCsv(true))

    try {
      // Create CSV headers
      let headers = [
        'User Name',
        'User Email',
        'Course Title',
        'Completion Date',
        'CE Hours',
        'Category',
        'License Type',
        'Activity Type',
        'Course Code',
        'Certificate ID',
        'Was Assigned',
        'Assignment Name',
        'Assignment Due Date',
        'Days Until/Past Due',
      ]

      // Create CSV rows
      let csvRows = ceActivities.map((activity) => {
        let daysDifference = ''
        if (activity.wasAssigned && activity.assignmentDueDate) {
          let daysFromDue = dayjs(activity.completionDate).diff(
            dayjs(activity.assignmentDueDate),
            'day',
          )
          // eslint-disable-next-line fp/no-mutation
          daysDifference =
            daysFromDue > 0
              ? `${daysFromDue} days past due`
              : `${Math.abs(daysFromDue)} days before due`
        }

        return [
          activity.userName,
          activity.userEmail,
          activity.courseTitle,
          dayjs(activity.completionDate).format('YYYY-MM-DD'),
          activity.ceHours.toString(),
          activity.category,
          activity.licenseTypes?.join(', ') || 'Unknown',
          activity.activityType,
          activity.courseCode || '',
          activity.certificateId || '',
          activity.wasAssigned ? 'Yes' : 'No',
          activity.assignmentName || '',
          activity.assignmentDueDate
            ? dayjs(activity.assignmentDueDate).format('YYYY-MM-DD')
            : '',
          daysDifference,
        ]
      })

      // Combine headers and rows
      let csvContent = [headers, ...csvRows]
        .map((row) => row.map((field) => `"${field}"`).join(','))
        .join('\n')

      // Create and download file
      let blob = new Blob([csvContent], { type: 'text/csv' })
      let linkedService = (linkedServices as any[]).find(
        (ls: any) => ls.id === selectedLinkedServiceID,
      )
      let serviceName = linkedService?.name || 'Unknown'
      let timeRangeLabel = timeRange === 'custom' ? 'Custom' : timeRange
      let fileName = `CE_Activities_${serviceName}_${timeRangeLabel}_${dayjs().format('YYYY-MM-DD')}.csv`

      downloadFile(blob, fileName)
      dispatch(setExportingCsv(false))
    } catch (err: any) {
      dispatch(setExportingCsv(false))
      dispatch(setErrorMessage(`CSV Export Failed: ${err.message || err}`))
    }
  }

  // All the original UI components remain exactly the same from here...
  // This is identical to the original component's rendering logic

  // Animated KPI cards
  let KPICard = ({
    title,
    value,
    trend,
    trendLabel,
    icon,
    delay = 0,
  }: {
    title: string
    value: string | number
    trend?: number
    trendLabel?: string | null
    icon: React.ReactNode
    delay?: number
  }) => (
    <Grow
      in={!isLoadingData}
      timeout={1000}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, my: 1 }}>
                {value}
              </Typography>
              {trend !== undefined && trendLabel && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: trend >= 0 ? 'success.main' : 'error.main',
                      transform: trend < 0 ? 'rotate(180deg)' : 'none',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: trend >= 0 ? 'success.main' : 'error.main' }}
                  >
                    {Math.abs(trend)}% {trendLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  )

  // Interactive Timeline Component
  let InteractiveTimeline = () => (
    <Paper
      sx={{ p: 3, maxHeight: 600, overflow: 'auto', position: 'relative' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <TimelineIcon />
          CE Activity Timeline
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            size="small"
            label={`${timelineData.length} days`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${ceActivities.length} activities`}
            color="primary"
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={
              exportingCsv ? <CircularProgress size={16} /> : <DownloadIcon />
            }
            disabled={exportingCsv || ceActivities.length === 0}
            onClick={exportFilteredActivitiesCSV}
            sx={{ minWidth: 'auto' }}
          >
            {exportingCsv ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Box>
      </Box>

      {timelineData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No CE activities found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or date range to see more data
          </Typography>
        </Box>
      ) : (
        <Timeline>
          {timelineData.map((dayData, index) => (
            <TimelineItem key={dayData.date}>
              <TimelineOppositeContent sx={{ flex: 0.3 }}>
                <Typography variant="body2" color="text.secondary">
                  {dayjs(dayData.date).format('MMM DD')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(dayData.date).format('dddd')}
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    fontWeight: 'bold',
                  }}
                >
                  {dayData.totalHours}h
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  color="primary"
                  sx={{
                    transform: `scale(${Math.min(2, 1 + dayData.totalHours / 8)})`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: `scale(${Math.min(2.5, 1.5 + dayData.totalHours / 8)})`,
                    },
                  }}
                />
                {index < timelineData.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ mb: 2 }}>
                  {/* Assignment Events */}
                  {dayData.assignmentEvents.map((assignmentEvent) => (
                    <Card
                      key={assignmentEvent.id}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        p: 1.5,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        bgcolor: 'background.paperDark',
                        borderColor: 'secondary.main',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)',
                          borderColor: 'secondary.dark',
                        },
                      }}
                      onClick={() => {
                        dispatch(setSelectedAssignmentEvent(assignmentEvent))
                        dispatch(setAssignmentEventOpen(true))
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'secondary.main',
                            }}
                          >
                            <AssignmentIcon sx={{ fontSize: 16 }} />
                            Assignment Created: {assignmentEvent.assignmentName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {assignmentEvent.userCount} users •{' '}
                            {assignmentEvent.courseCount} courses
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            Due:{' '}
                            {dayjs(assignmentEvent.dueDate).format(
                              'MMM DD, YYYY',
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label="Assignment"
                            color="secondary"
                            variant="filled"
                          />
                        </Box>
                      </Box>
                    </Card>
                  ))}

                  {/* CE Activities */}
                  {dayData.activities.slice(0, 3).map((activity) => (
                    <Card
                      key={activity.id}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        p: 1.5,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)',
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => {
                        dispatch(setSelectedUserForDetails(activity.userId))
                        dispatch(setUserDetailsOpen(true))
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.5 }}
                          >
                            {activity.courseTitle}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {activity.userName} • {activity.category}
                          </Typography>
                          {activity.courseCode && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              Code: {activity.courseCode}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label={`${activity.ceHours}h`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={activity.activityType}
                            color={
                              activity.activityType === 'recorded'
                                ? 'info'
                                : activity.activityType === 'live'
                                  ? 'warning'
                                  : 'default'
                            }
                            variant="filled"
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {activity.certificateId && (
                          <Chip
                            size="small"
                            label="Certificate Available"
                            color="success"
                            variant="outlined"
                            icon={<CertificateIcon />}
                          />
                        )}
                        {activity.wasAssigned && (
                          <Chip
                            size="small"
                            label={`Assigned: ${activity.assignmentName}`}
                            color="secondary"
                            variant="outlined"
                            title={
                              activity.assignmentDueDate
                                ? `Due: ${dayjs(activity.assignmentDueDate).format('MMM DD, YYYY')}`
                                : undefined
                            }
                          />
                        )}
                      </Box>
                    </Card>
                  ))}
                  {(dayData.activities.length > 3 ||
                    dayData.assignmentEvents.length > 0) && (
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => {
                        dispatch(setSelectedDayData(dayData))
                        dispatch(setDayActivitiesOpen(true))
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {dayData.activities.length > 3 &&
                          `+${dayData.activities.length - 3} more activities`}
                        {dayData.activities.length > 3 &&
                          dayData.assignmentEvents.length > 0 &&
                          ' • '}
                        {dayData.assignmentEvents.length > 0 &&
                          'View all events'}
                      </Typography>
                    </Card>
                  )}
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Paper>
  )

  // [Continue with rest of the UI components - this would be a very long file]
  // For now, I'll add a placeholder for the main render return

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Advanced Activity Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive continuing education analytics with interactive
            timelines and advanced reporting
          </Typography>
        </Box>

        {/* Comprehensive Filter Panel */}
        <FilterPanel />

        {/* KPI Cards */}
        <Box className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 md:grid-cols-3">
          <KPICard
            title="Total Users"
            value={kpiData?.totalUsers || 0}
            icon={<UserIcon />}
            delay={0}
          />
          <KPICard
            title="Total CE Hours"
            value={kpiData?.totalHours || 0}
            trendLabel={kpiData?.trendLabel}
            icon={<CertificateIcon />}
            delay={200}
          />
          <KPICard
            title="Avg Hours/User"
            value={(kpiData?.avgHoursPerUser || 0).toFixed(1)}
            icon={<TrendingUpIcon />}
            delay={400}
          />
        </Box>

        {/* Main Content */}
        <Slide direction="up" in={true} timeout={800}>
          <Box>
            {!selectedLinkedServiceID ? (
              <Paper sx={{ p: 8, textAlign: 'center' }}>
                <SearchIcon
                  sx={{
                    fontSize: 64,
                    mb: 2,
                    opacity: 0.5,
                    color: 'text.secondary',
                  }}
                />
                <Typography
                  color="text.secondary"
                  variant="h5"
                  sx={{
                    mb: 2,
                  }}
                >
                  Select a Linked Service
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose a linked service from the dropdown above to view CE
                  analytics and reports.
                </Typography>
              </Paper>
            ) : (
              <>
                {viewMode === 'timeline' && <InteractiveTimeline />}

                {/* View Components */}
                {viewMode === 'analytics' && <AnalyticsView />}
                {viewMode === 'summary' && <SummaryView />}
                {viewMode === 'export' && <ExportView />}
              </>
            )}
          </Box>
        </Slide>

        {/* Error Snackbar */}
        <Snackbar
          open={!!errorMessage}
          onClose={() => dispatch(clearMessages())}
        >
          <Alert
            variant="filled"
            onClose={() => dispatch(clearMessages())}
            severity="error"
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        {/* Alert Snackbar for filter changes */}
        <Snackbar
          open={!!alertMessage}
          onClose={() => dispatch(clearMessages())}
          autoHideDuration={4000}
        >
          <Alert
            variant="filled"
            onClose={() => dispatch(clearMessages())}
            severity="info"
          >
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Modal Components */}
        <UserDetailsModal onDownloadCertificate={downloadCertificate} />
        <DayActivitiesModal onDownloadCertificate={downloadCertificate} />
        <AssignmentEventModal />
      </Container>
    </LocalizationProvider>
  )
}
